import request from './request';

const API = 'https://server.battleverse.cn/fight';
const jobs = new Map();
const listeners = new Set();
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
const owner = () => {
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return String(u.id || u.userId || ''); }
  catch { return ''; }
};
const publish = () => listeners.forEach(fn => fn([...jobs.values()].filter(j => j.record.owner === owner()).map(j => ({ ...j.view, requestId: j.record.requestId }))));
let database;
async function db() {
  if (!database) database = new Promise((resolve, reject) => {
    const open = indexedDB.open('pb-fight-queue', 1);
    open.onupgradeneeded = () => open.result.createObjectStore('jobs', { keyPath: 'requestId' });
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
    open.onblocked = () => reject(new Error('本机存储暂不可用'));
  });
  try { return await database; } catch (e) { database = null; throw e; }
}
async function stored(action, record) {
  const database = await db();
  return new Promise((resolve, reject) => {
    const tx = database.transaction('jobs', action === 'getAll' ? 'readonly' : 'readwrite');
    const store = tx.objectStore('jobs');
    const operation = action === 'getAll' ? store.getAll() : action === 'put' ? store.put(record) : store.delete(record);
    tx.oncomplete = () => resolve(operation.result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('本机存储写入失败'));
  });
}
async function persist(record) {
  record.storageUnavailable = false;
  try { await stored('put', record); return true; }
  catch { record.storageUnavailable = true; return false; }
}
window.addEventListener('beforeunload', event => {
  if ([...jobs.values()].some(j => j.record.owner === owner() && j.record.kind === 'upload'
    && j.record.file && j.record.storageUnavailable && !['DONE','FAILED','CANCELLED'].includes(j.view.state))) {
    event.preventDefault(); event.returnValue = '';
  }
});
const post = (path, body) => request.post(`${API}/queue/${path}.do`, body, { timeout: 12000 });
const update = (job, value) => {
  job.view = { ...job.view, ...value, ...(value.attentionRemainingMs != null ? { attentionReceivedAt: performance.now() } : {}) };
  publish();
};

async function run(job) {
  const r = job.record;
  job.transfer = null;
  for (;;) {
    if (owner() !== r.owner || !localStorage.getItem('token')) throw Object.assign(new Error('请登录原账号查看提交结果'), { resumeOnLogin: true });
    try {
      const firstAdmission = !r.ticketId;
      const reply = r.ticketId
        ? await post('status', { ticketId: r.ticketId })
        : await post('join', { kind: r.kind, fightId: r.fightId, requestId: r.requestId, creationId: r.creationId, status: r.status });
      let state = reply.data;
      if (!r.ticketId) { r.ticketId = state.ticketId; await persist(r); }
      if (r.kind === 'upload' && !r.file && ['WAITING','READY','RETRY','ATTENTION'].includes(state.state)) {
        state = (await post('missing-file', { ticketId: r.ticketId })).data;
      }
      update(job, { ...state, storageUnavailable: r.storageUnavailable, reconnecting: false });
      if (state.state === 'DONE') {
        try { await stored('delete', r.requestId); } catch {}
        return state.result || { code: 0, success: true, msg: state.message };
      }
      if (state.state === 'FAILED' || state.state === 'CANCELLED') {
        try { await stored('delete', r.requestId); } catch {}
        throw Object.assign(new Error(state.message || '提交未完成'), { terminal: true, queueState: state.state, cancelled: state.state === 'CANCELLED' });
      }
      if (r.kind === 'upload' && state.state === 'READY' && !job.transfer) {
        if (!r.file) throw new Error('请重新选择图片');
        const form = new FormData();
        form.append('upfile', r.file, r.file.name || 'creation.png');
        form.append('ticketId', r.ticketId);
        update(job, { progress: 0, sending: true });
        // Poll separately while bytes are sent. A timeout is not treated as a confirmed failure.
        job.transfer = request.post(`${API}/upload.do`, form, {
          headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000,
          onUploadProgress: e => update(job, { progress: e.total ? Math.round(e.loaded * 100 / e.total) : null, sending: true })
        }).catch(() => {}).finally(() => { job.transfer = null; update(job, { sending: false }); });
      }
      if (state.state === 'RETRY' && !job.transfer && (r.kind !== 'upload' || r.file)) {
        await post('retry', { ticketId: r.ticketId });
      }
      if (firstAdmission && r.kind === 'vote') continue;
    } catch (error) {
      if (error.terminal || error.code === -1 || error.code === -2) throw error;
      update(job, { message: '连接暂时中断，正在确认提交结果，请保持页面打开', reconnecting: true });
      await sleep(2000);
    }
    await sleep(1200 + Math.random() * 800);
  }
}
function start(record) {
  const previous = jobs.get(record.requestId);
  if (previous && !previous.resumeOnLogin) return previous.promise;
  const job = { record, view: { kind: record.kind, state: 'CONNECTING', storageUnavailable: record.storageUnavailable, message: '正在确认排队资格' } };
  jobs.set(record.requestId, job); publish();
  job.promise = run(job).then(result => {
    update(job, { state: 'DONE', message: record.kind === 'upload' ? '上传成功' : '投票已提交', reconnecting: false });
    window.dispatchEvent(new CustomEvent('pb-queue-completed', { detail: { kind: record.kind, fightId: record.fightId } }));
    return result;
  }).catch(async error => {
    if (error.code === -1 || error.terminal) { try { await stored('delete', record.requestId); } catch {} }
    job.resumeOnLogin = error.resumeOnLogin || error.code === -2;
    update(job, { state: error.queueState || 'FAILED', message: error.message, reconnecting: false });
    throw error;
  });
  return job.promise;
}
function validateFile(file) {
  if (!file || file.size > 20 * 1024 * 1024) throw new Error('请选择不超过 20 MB 的图片');
  if (!/\.(png|jpe?g|webp|gif)$/i.test(file.name || '')) throw new Error('请选择 PNG、JPG、WEBP 或 GIF 图片');
}
export async function queueUpload(file, fightId) {
  validateFile(file);
  const record = { requestId: crypto.randomUUID(), owner: owner(), kind: 'upload', fightId, file };
  await persist(record);
  return start(record);
}
export async function queueVote(input) {
  const record = { ...input, requestId: crypto.randomUUID(), owner: owner(), kind: 'vote' };
  await persist(record);
  return start(record);
}
export function subscribeQueue(listener) { listeners.add(listener); publish(); return () => listeners.delete(listener); }
export async function restoreQueue() {
  publish();
  if (!owner() || !localStorage.getItem('token')) return;
  const restoringOwner = owner();
  let saved = [];
  try { saved = await stored('getAll'); } catch {}
  if (owner() !== restoringOwner) return;
  for (const r of saved) if (r.owner === restoringOwner) start(r).catch(() => {});
  try {
    const response = await post('mine', {});
    if (owner() !== restoringOwner) return;
    for (const r of response.data || []) {
      if ([...jobs.values()].some(j => j.record.ticketId === r.ticketId && !j.resumeOnLogin)) continue;
      start({ requestId: r.requestId, owner: restoringOwner, kind: r.kind, fightId: r.fightId,
        ticketId: r.ticketId, creationId: r.creationId, status: r.status, storageUnavailable: true }).catch(() => {});
    }
  } catch { /* Existing records continue their own bounded polling after a network interruption. */ }
}
export async function resumeQueueUpload(requestId, file) {
  validateFile(file);
  const job = jobs.get(requestId);
  if (!job || job.record.owner !== owner() || job.record.kind !== 'upload') throw new Error('排队记录不存在');
  job.record.file = file;
  await persist(job.record);
  const reply = await post('retry', { ticketId: job.record.ticketId, fileSelected: true });
  update(job, { ...reply.data, storageUnavailable: job.record.storageUnavailable });
}
export async function cancelQueue(requestId) {
  const job = jobs.get(requestId);
  if (!job?.record.ticketId) return;
  try {
    const reply = await post('cancel', { ticketId: job.record.ticketId });
    if (reply.data.state === 'CANCELLED') {
      try { await stored('delete', requestId); } catch {}
    }
    update(job, reply.data);
  }
  catch (e) { update(job, { message: e.message }); }
}
export async function retryQueue(requestId) {
  const job = jobs.get(requestId);
  if (!job?.record.ticketId) return;
  try { const reply = await post('retry', { ticketId: job.record.ticketId }); update(job, reply.data); }
  catch (e) { update(job, { message: e.message }); }
}
export function dismissQueue(requestId) { jobs.delete(requestId); publish(); }
export const queueOverview = () => post('overview', {});
